/**
 * MDXEditor portals its popups (the block-type dropdown, the link dialog) into a container it
 * appends to `document.body`; the editor wrapper re-parents that container next to the editor so it
 * is not stuck below a dialog's z-index. Closing such a popup still tears the surrounding modal
 * down, though: Radix flips `pointer-events` on the body while the dropdown is open and restores
 * them on close, so the click that dismissed it surfaces at the document, where Headless UI reads it
 * as a click outside the panel.
 *
 * Watching for pointer events inside the editor does not catch that — the dismissing click often
 * lands somewhere else entirely. The reliable signal is the popup's own teardown, which the editor
 * wrapper reports here so any modal can tell it apart from the operator actually leaving.
 *
 * This lives in `ui` rather than next to the editor in `composables` on purpose: `Modal` is in `ui`,
 * and `ui` importing from `composables` would close a dependency cycle.
 */

/** How long after a popup closed a close request is still attributed to that popup. */
const GRACE_PERIOD_MS = 400;

let closedAt = 0;

/** Called by the MDX editor wrapper when its popup container went from occupied to empty. */
export function markMdxPopupClosed(): void {
    closedAt = Date.now();
}

/**
 * True while an MDX editor popup is on screen, or was dismissed a moment ago — i.e. a close request
 * arriving now came from the popup rather than from the operator wanting to leave the modal.
 */
export function isMdxPopupOpenOrJustClosed(): boolean {
    if (typeof document !== 'undefined') {
        const containers = document.querySelectorAll<HTMLElement>('.mdxeditor-popup-container');
        for (const container of containers) {
            if (container.childElementCount > 0) {
                return true;
            }
        }
    }
    return Date.now() - closedAt < GRACE_PERIOD_MS;
}
